import { Banknote, Check, Plus, Printer, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  ChipToggle,
  Dialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import {
  useBill,
  useBillActions,
  useBillDeposit,
} from '@/features/billing/api';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import {
  APPROVAL_KIND_LABEL,
  LINE_KIND_LABEL,
  METHOD_LABEL,
  REASON_LABEL,
  type Bill,
  type BillLine,
} from '@/features/billing/types';
import { usePayers } from '@/features/patients/api';
import { isApiError } from '@/lib/api';
import { BillingLimitBanner } from '@/features/limit-billing/BillingLimitBanner';
import { LimitBillingAudit } from '@/features/limit-billing/LimitBillingAudit';
import { OverLimitPatientPaySplit } from '@/features/limit-billing/OverLimitPatientPaySplit';
import { PanelApprovalOverrideControl } from '@/features/limit-billing/PanelApprovalOverrideControl';
import {
  isStaleLimitAssessmentError,
  useLimitBillingAssessment,
  type LimitAssessmentConfirmation,
} from '@/features/limit-billing/api';
import styles from '@/pages/queue/queue.module.css';
import { AddLineDialog } from './AddLineDialog';
import { BillStatusBadge } from './BillStatusBadge';
import { DiscountDialog, RefundDialog } from './BillRequests';
import { DuitNowQr, PaymentDialog } from './PaymentDialog';
import { ReceiptDialog } from './ReceiptDialog';

/** "Bill and payer split" (artifacts #30–31). */
export function BillDrawer({
  billId,
  onClose,
}: {
  billId: string | null;
  onClose: () => void;
}) {
  const { selected } = useBranch();
  const { data: b, isLoading, error, refetch } = useBill(billId);
  return (
    <Dialog
      open={!!billId}
      onClose={onClose}
      wide
      title={b ? `${b.number} · ${b.patient.fullName}` : 'Bill'}
    >
      {isLoading ? (
        <LoadingState />
      ) : error || !b ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <BillBody
          key={`${b.id}:${b.version}:${selected?.id ?? 'all'}`}
          bill={b}
        />
      )}
    </Dialog>
  );
}

export function BillBody({ bill: b }: { bill: Bill }) {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const { selected, branches, select } = useBranch();
  const atBillBranch = selected?.id === b.branch.id;
  const canSelectBillBranch = branches.some(
    (branch) => branch.id === b.branch.id,
  );
  const hasBillActions =
    hasPermission('bills:manage') ||
    hasPermission('payments:record') ||
    hasPermission('panel_limits:override');
  const a = useBillActions(b.id);
  const toast = useToast();
  const assessment = useLimitBillingAssessment(
    b.payer ? b.id : null,
    b.status === 'open',
  );
  const [paying, setPaying] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [adding, setAdding] = useState(false);
  const [discounting, setDiscounting] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [receipt, setReceipt] = useState(false);
  const [reason, setReason] = useState('');
  const [paymentError, setPaymentError] = useState<unknown>();
  const [assessmentEpoch, setAssessmentEpoch] = useState(0);
  const [confirmation, setConfirmation] = useState({
    signature: '',
    checked: false,
  });
  const pendingVoid = !!b.voidRequest && !b.voidRequest.decidedAt;
  const pendingDiscount = b.approvals.some(
    (x) => x.kind === 'discount' && x.status === 'pending',
  );
  const editable =
    atBillBranch &&
    b.status === 'open' &&
    !pendingVoid &&
    hasPermission('bills:manage');
  const canReadPayers =
    hasPermission('patients:read') ||
    hasPermission('queue:read') ||
    hasPermission('my_patients:read');
  const { data: payers } = usePayers(editable && canReadPayers);
  const canPay =
    atBillBranch &&
    b.status === 'open' &&
    !pendingVoid &&
    hasPermission('payments:record');
  const deposit = useBillDeposit(b.id, b.status === 'open' && !!b.encounter);
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: isApiError(err) ? (err.problem?.title ?? 'Not done') : 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
  const payerName = b.payer?.name ?? 'Panel';
  const malaysian = b.patient.nationality === 'MY';
  const refundable = b.paidSen - b.refundedSen - b.refundPendingSen;
  const assessmentData = assessment.data;
  const assessmentSignature = assessmentData
    ? [
        assessmentData.id,
        assessmentData.status,
        assessmentData.split.panelCoverageSen,
        assessmentData.split.patientPaySen,
        assessmentEpoch,
      ].join(':')
    : '';
  const assessmentConfirmed =
    confirmation.signature === assessmentSignature && confirmation.checked;
  const limitConfirmation: LimitAssessmentConfirmation | undefined =
    assessmentData && assessmentConfirmed
      ? {
          assessmentId: assessmentData.id,
          confirmed: true,
          panelCoverageSen: assessmentData.split.panelCoverageSen,
          patientPaySen: assessmentData.split.patientPaySen,
        }
      : undefined;
  const assessmentReady =
    !b.payer ||
    (!!limitConfirmation && !assessment.isLoading && !assessment.error);

  const refreshAssessmentAfterConflict = useCallback(
    async (error: unknown) => {
      if (!isStaleLimitAssessmentError(error)) return;
      setAssessmentEpoch((value) => value + 1);
      setPaymentError(error);
      await assessment.refetch();
      setPaymentError(undefined);
    },
    [assessment],
  );

  const retryAssessment = () => {
    setAssessmentEpoch((value) => value + 1);
    setPaymentError(undefined);
    void assessment.refetch();
  };

  // RL-1745 (review fix, RL-1950): the API sets the bill's panel share from
  // the limit assessment whenever the bill is built or changed (RL-1744), and
  // applies a missing over-limit split itself with the confirmed payment. The
  // drawer only shows the split the API returns; it never applies one.

  const capToastKey = useRef<string | null>(null);
  useEffect(() => {
    if (!assessmentData || (!paying && b.balanceSen !== 0)) return;
    if (
      assessmentData.perVisitCapSen === null ||
      assessmentData.split.eligibleTotalSen <= assessmentData.perVisitCapSen
    )
      return;
    const key = `${assessmentData.id}:${assessmentData.perVisitCapSen}`;
    if (capToastKey.current === key) return;
    capToastKey.current = key;
    toast.show({
      tone: 'info',
      title: t('limitBilling.perVisitCapExceeded'),
      body: t('limitBilling.perVisitCapExceededBody', {
        eligible: formatRM(assessmentData.split.eligibleTotalSen),
        cap: formatRM(assessmentData.perVisitCapSen),
      }),
    });
  }, [assessmentData, b.balanceSen, paying, t, toast]);

  const handleCheckoutError = (checkoutError: unknown) => {
    setPaymentError(checkoutError);
    if (isStaleLimitAssessmentError(checkoutError)) {
      void refreshAssessmentAfterConflict(checkoutError);
    }
  };

  return (
    <div className={styles.drawer}>
      {!atBillBranch && canSelectBillBranch && hasBillActions && (
        <Button onClick={() => select(b.branch.id)}>
          {t('billing.selectBillBranch', { branch: b.branch.name })}
        </Button>
      )}
      <dl className={styles.rows}>
        <dt>{t('shell.branch')}</dt>
        <dd>{b.branch.name}</dd>
        {b.queueNumber !== null && (
          <>
            <dt>Queue</dt>
            <dd>{b.queueNumber}</dd>
          </>
        )}
        <dt>Payer</dt>
        <dd>
          {editable ? (
            <Select
              aria-label="Payer"
              value={b.payer?.id ?? ''}
              options={[
                { value: '', label: 'Self-pay' },
                ...(payers ?? []).map((p) => ({ value: p.id, label: p.name })),
              ]}
              onChange={(ev) =>
                void a.update
                  .mutateAsync({
                    version: b.version,
                    payerId: ev.target.value || null,
                  })
                  .catch(fail)
              }
            />
          ) : b.payer ? (
            `${b.payer.name}${b.memberNo ? ` · ${b.memberNo}` : ''}`
          ) : (
            'Self-pay'
          )}
          {editable && b.memberNo && (
            <span className={styles.sub}> · {b.memberNo}</span>
          )}
        </dd>
        <dt>Patient</dt>
        <dd>
          {malaysian ? 'Malaysian' : `Non-Malaysian (${b.patient.nationality})`}
        </dd>
        <dt>Member price</dt>
        <dd>
          {editable ? (
            <ChipToggle
              label="Member price applies"
              single
              options={[
                { value: 'no', label: 'No' },
                { value: 'yes', label: 'Yes' },
              ]}
              selected={[b.memberPrice ? 'yes' : 'no']}
              onChange={(v) =>
                v[0] &&
                (v[0] === 'yes') !== b.memberPrice &&
                void a.update
                  .mutateAsync({
                    version: b.version,
                    memberPrice: v[0] === 'yes',
                  })
                  .catch(fail)
              }
            />
          ) : b.memberPrice ? (
            'Applies'
          ) : (
            'No'
          )}
        </dd>
        <dt>Status</dt>
        <dd className={styles.badges}>
          <BillStatusBadge bill={b} />
          {b.receiptNumber && (
            <span className="mono">Receipt {b.receiptNumber}</span>
          )}
        </dd>
        {b.encounter && (
          <>
            <dt>Doctor</dt>
            <dd>{b.encounter.doctor.fullName}</dd>
          </>
        )}
        {b.refill && (
          <>
            <dt>Refill</dt>
            <dd className={styles.badges}>
              <Badge tone="info">Refill</Badge>
              <span>
                <span className="mono">
                  {b.refill.prescriptionNumber ?? 'Prescription'}
                </span>
                {b.refill.originalPrescription?.number && (
                  <>
                    {' '}
                    repeats{' '}
                    <span className="mono">
                      {b.refill.originalPrescription.number}
                    </span>
                  </>
                )}
              </span>
            </dd>
          </>
        )}
      </dl>

      <div className={styles.sectionHead}>
        <p className="eyebrow">Lines</p>
        {editable && (
          <Button
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => setAdding(true)}
          >
            Add line
          </Button>
        )}
      </div>
      <table className={styles.billTable} aria-label="Bill lines">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Unit price</th>
            <th>Amount</th>
            {editable && <th aria-label="Remove" />}
          </tr>
        </thead>
        <tbody>
          {b.lines.map((l) => (
            <LineRow
              key={`${l.id}:${l.unitPriceSen}:${l.quantity}:${String(l.covered)}`}
              line={l}
              payerName={b.payer ? payerName : null}
              editable={editable}
              onPrice={(unitPriceSen, reason) =>
                a.updateLine
                  .mutateAsync({
                    lineId: l.id,
                    unitPriceSen,
                    reason,
                    version: b.version,
                  })
                  .catch(fail)
              }
              onQuantity={(quantity) =>
                a.updateLine
                  .mutateAsync({ lineId: l.id, quantity, version: b.version })
                  .catch(fail)
              }
              onCovered={(covered) =>
                a.updateLine
                  .mutateAsync({ lineId: l.id, covered, version: b.version })
                  .catch(fail)
              }
              onRemove={() => a.removeLine.mutateAsync(l.id).catch(fail)}
            />
          ))}
        </tbody>
      </table>

      <div className={styles.section}>
        <p className="eyebrow">Payer split</p>
        <dl className={styles.rows} aria-label="Payer split">
          {b.payer && (
            <>
              <dt>{payerName} pays</dt>
              <dd>{formatRM(b.panelSen)}</dd>
              <dt>Patient share (excluded items)</dt>
              <dd>{formatRM(b.patientLinesSen)}</dd>
            </>
          )}
          {b.memberSavingSen > 0 && (
            <>
              <dt>Member price</dt>
              <dd className={styles.saving}>-{formatRM(b.memberSavingSen)}</dd>
            </>
          )}
          {b.discountSen > 0 && (
            <>
              <dt>Discount (approved)</dt>
              <dd className={styles.saving}>-{formatRM(b.discountSen)}</dd>
            </>
          )}
          {b.taxSen > 0 && (
            <>
              <dt>Service tax</dt>
              <dd>{formatRM(b.taxSen)}</dd>
            </>
          )}
          <dt>Patient pays</dt>
          <dd>
            <strong>{formatRM(b.totalSen)}</strong>
          </dd>
          <dt>Paid</dt>
          <dd>{formatRM(b.paidSen)}</dd>
          <dt>Patient pays now</dt>
          <dd data-testid="balance">
            <strong>{formatRM(b.balanceSen)}</strong>
          </dd>
          {b.refundedSen > 0 && (
            <>
              <dt>Refunded</dt>
              <dd>{formatRM(b.refundedSen)}</dd>
            </>
          )}
        </dl>
      </div>

      {b.payer && assessmentData && (
        <OverLimitPatientPaySplit
          split={assessmentData.split}
          status={assessmentData.status}
        />
      )}

      {atBillBranch &&
        b.payer &&
        assessmentData &&
        hasPermission('panel_limits:override') && (
          <PanelApprovalOverrideControl
            assessment={assessmentData}
            onAssessmentStale={(overrideError) =>
              void refreshAssessmentAfterConflict(overrideError)
            }
          />
        )}

      {b.payer && assessmentData && (
        <LimitBillingAudit entries={assessmentData.audit} />
      )}

      {canPay &&
        deposit.data &&
        deposit.data.availableSen > 0 &&
        deposit.data.appliedSen === 0 && (
          <p className={styles.warnNote}>
            Booking deposit {formatRM(deposit.data.availableSen)} was paid for
            this visit.{' '}
            <Button
              size="sm"
              loading={a.applyDeposit.isPending}
              disabled={!!b.payer && !assessmentReady}
              onClick={() =>
                a.applyDeposit
                  .mutateAsync({ limitAssessment: limitConfirmation })
                  .catch(handleCheckoutError)
              }
            >
              Apply deposit
            </Button>
          </p>
        )}

      {b.payments.length > 0 && (
        <div className={styles.section}>
          <p className="eyebrow">Payments</p>
          <ul className={styles.addenda}>
            {b.payments.map((p) => (
              <li key={p.id}>
                {p.online
                  ? t('onlinePayments.billMethod', {
                      provider:
                        p.online.provider === 'stripe'
                          ? 'Stripe'
                          : p.online.provider,
                      method:
                        p.method === 'e_wallet'
                          ? t('onlinePayments.methods.grabPay')
                          : t('onlinePayments.methods.card'),
                    })
                  : METHOD_LABEL[p.method]}{' '}
                {formatRM(p.amountSen)}
                {p.changeSen !== null && p.changeSen > 0
                  ? ` · change ${formatRM(p.changeSen)}`
                  : ''}
                {p.reference ? ` · ${p.reference}` : ''}
                <span className={styles.sub}>
                  {' '}
                  · {new Date(p.receivedAt).toLocaleTimeString('en-GB')}
                  {p.receivedBy.id !== null && <> · {p.receivedBy.fullName}</>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {b.duitNow && b.status === 'open' && (
        <div className={styles.qrPanel} aria-label="DuitNow QR">
          <DuitNowQr payload={b.duitNow.payload} />
          <div>
            <p>
              <strong>{formatRM(b.duitNow.amountSen)}</strong> · ref{' '}
              <span className="mono">{b.duitNow.reference}</span>
            </p>
            <p className={styles.sub}>
              Waiting for the bank. The bill updates by itself when the bank
              confirms.
            </p>
            {b.duitNow.simulated && canPay && (
              <Button
                size="sm"
                loading={a.confirmDuitNow.isPending}
                onClick={() =>
                  a.confirmDuitNow
                    .mutateAsync({
                      requestId: b.duitNow!.id,
                      limitAssessment: limitConfirmation,
                    })
                    .then((nb) =>
                      toast.show({
                        tone: 'success',
                        title:
                          nb.status === 'closed'
                            ? `Paid by DuitNow · ${nb.receiptNumber ?? ''}`
                            : 'DuitNow payment received',
                      }),
                    )
                    .catch(fail)
                }
              >
                Simulate bank confirmation (demo)
              </Button>
            )}
          </div>
        </div>
      )}

      {b.approvals.length > 0 && (
        <div className={styles.section}>
          <p className="eyebrow">Requests</p>
          <ul className={styles.addenda}>
            {b.approvals.map((x) => (
              <li key={x.id}>
                {APPROVAL_KIND_LABEL[x.kind]} {formatRM(x.amountSen)} ·{' '}
                {REASON_LABEL[x.reason] ?? x.reason}
                {x.refundMethod
                  ? ` · back by ${METHOD_LABEL[x.refundMethod]}`
                  : ''}{' '}
                <Badge
                  tone={
                    x.status === 'pending'
                      ? 'warning'
                      : x.status === 'approved'
                        ? 'success'
                        : 'danger'
                  }
                >
                  {x.status === 'pending'
                    ? 'Waiting for the manager'
                    : x.status === 'approved'
                      ? 'Approved'
                      : 'Rejected'}
                </Badge>
                {x.decisionNote && (
                  <span className={styles.sub}> · {x.decisionNote}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {b.claim && (
        <p className={styles.sub}>
          Draft claim to {payerName}: {formatRM(b.claim.amountSen)} (Finance
          submits it).
        </p>
      )}

      {b.voidRequest && (
        <p className={pendingVoid ? styles.warnNote : styles.sub}>
          {pendingVoid
            ? `Void requested by ${b.voidRequest.requestedBy?.fullName ?? '—'}: ${b.voidRequest.reason}. Waiting in Approvals; nothing can change until the manager decides.`
            : b.voidRequest.approved
              ? `Voided by ${b.voidRequest.decidedBy?.fullName ?? '—'} (${b.voidRequest.reason}).`
              : `Void rejected by ${b.voidRequest.decidedBy?.fullName ?? '—'}: ${b.voidRequest.note ?? ''}`}
        </p>
      )}

      {pendingDiscount && (
        <p className={styles.warnNote}>
          A discount is waiting for the manager. Take payment once it is
          decided.
        </p>
      )}

      {b.status === 'open' && b.unpricedLines > 0 && !pendingVoid && (
        <p className={styles.warnNote}>
          {b.unpricedLines} line(s) need a price (not in Stock › Prices or the
          fee schedule). Set the price before taking payment.
        </p>
      )}

      {canPay &&
        b.payer &&
        (b.balanceSen === 0 ||
          (!!deposit.data &&
            deposit.data.availableSen > 0 &&
            deposit.data.appliedSen === 0)) && (
          <BillingLimitBanner
            assessment={assessmentData}
            loading={assessment.isLoading}
            error={paymentError ?? assessment.error}
            confirmed={assessmentConfirmed}
            onConfirmedChange={(checked) => {
              setPaymentError(undefined);
              setConfirmation({ signature: assessmentSignature, checked });
            }}
            onRetry={retryAssessment}
          />
        )}

      <div className={styles.consultFooter}>
        {editable && b.paidSen === 0 && (
          <Button variant="ghost" onClick={() => setVoiding(true)}>
            Void
          </Button>
        )}
        {canPay && !pendingDiscount && b.balanceSen > 0 && (
          <Button onClick={() => setDiscounting(true)}>Discount</Button>
        )}
        {b.status === 'closed' &&
          atBillBranch &&
          hasPermission('payments:record') &&
          refundable > 0 && (
            <Button onClick={() => setRefunding(true)}>Refund</Button>
          )}
        {b.status === 'closed' && (
          <Button icon={<Printer size={16} />} onClick={() => setReceipt(true)}>
            Receipt
          </Button>
        )}
        {canPay && b.balanceSen === 0 && (
          <Button
            variant="primary"
            icon={<Check size={16} />}
            disabled={pendingDiscount || !assessmentReady}
            loading={a.close.isPending}
            onClick={() =>
              a.close
                .mutateAsync({
                  version: b.version,
                  limitAssessment: limitConfirmation,
                })
                .then((nb) =>
                  toast.show({
                    tone: 'success',
                    title: nb.claim
                      ? `Closed · draft claim ${formatRM(nb.claim.amountSen)} created`
                      : `Closed · ${nb.receiptNumber ?? nb.number}`,
                  }),
                )
                .catch(handleCheckoutError)
            }
          >
            {b.panelSen > 0 ? 'Take payment & create claim' : 'Close bill'}
          </Button>
        )}
        {canPay && b.balanceSen > 0 && (
          <Button
            variant="primary"
            icon={<Banknote size={16} />}
            disabled={pendingDiscount}
            onClick={() => setPaying(true)}
          >
            {b.panelSen > 0 ? 'Take payment & create claim' : 'Take payment'}
          </Button>
        )}
      </div>

      <PaymentDialog
        open={paying}
        bill={b}
        busy={a.pay.isPending || a.duitNow.isPending}
        onClose={() => {
          setPaying(false);
          setPaymentError(undefined);
        }}
        assessment={assessmentData}
        assessmentLoading={assessment.isLoading}
        assessmentError={assessment.error}
        assessmentConfirmed={assessmentConfirmed}
        onAssessmentConfirmedChange={(checked) => {
          setPaymentError(undefined);
          setConfirmation({ signature: assessmentSignature, checked });
        }}
        onRetryAssessment={retryAssessment}
        paymentError={paymentError}
        onDuitNow={(amountSen, limitAssessment) =>
          a.duitNow
            .mutateAsync({ amountSen, limitAssessment })
            .then(() => setPaying(false))
            .catch(handleCheckoutError)
        }
        onPay={(input) =>
          a.pay
            .mutateAsync(input)
            .then((nb) => {
              setPaying(false);
              toast.show({
                tone: 'success',
                title:
                  nb.status === 'closed'
                    ? `Paid in full · ${nb.receiptNumber ?? nb.number}${nb.claim ? ' · claim created' : ''}`
                    : `Payment recorded · ${formatRM(nb.balanceSen)} left`,
              });
            })
            .catch(handleCheckoutError)
        }
      />
      {adding && (
        <AddLineDialog
          bill={b}
          onClose={() => setAdding(false)}
          onError={fail}
        />
      )}
      {discounting && (
        <DiscountDialog
          bill={b}
          onClose={() => setDiscounting(false)}
          onError={fail}
        />
      )}
      {refunding && (
        <RefundDialog
          bill={b}
          maxSen={refundable}
          onClose={() => setRefunding(false)}
          onError={fail}
        />
      )}
      {receipt && (
        <ReceiptDialog billId={b.id} onClose={() => setReceipt(false)} />
      )}
      <Dialog
        open={voiding}
        variant="modal"
        title={`Void ${b.number}`}
        onClose={() => setVoiding(false)}
        footer={
          <>
            <Button onClick={() => setVoiding(false)}>Back</Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              loading={a.requestVoid.isPending}
              onClick={() =>
                a.requestVoid
                  .mutateAsync(reason.trim())
                  .then(() => {
                    setVoiding(false);
                    toast.show({
                      tone: 'success',
                      title: 'Sent to the manager for approval',
                    });
                  })
                  .catch(fail)
              }
            >
              Send for approval
            </Button>
          </>
        }
      >
        <Field label="Reason and a note for the manager">
          <Input value={reason} onChange={(ev) => setReason(ev.target.value)} />
        </Field>
        <p className={styles.sub}>
          The bill waits in Approvals; nothing moves until the manager decides.
        </p>
      </Dialog>
    </div>
  );
}

function LineRow({
  line: l,
  payerName,
  editable,
  onPrice,
  onQuantity,
  onCovered,
  onRemove,
}: {
  line: BillLine;
  payerName: string | null;
  editable: boolean;
  onPrice: (sen: number, reason?: string) => void;
  onQuantity: (qty: number) => void;
  onCovered: (covered: boolean) => void;
  onRemove: () => void;
}) {
  const [price, setPrice] = useState(senToInput(l.unitPriceSen));
  const [qty, setQty] = useState(String(l.quantity));
  // RL-1743: a line that already has a price changes only with a reason.
  const [pendingSen, setPendingSen] = useState<number | null>(null);
  const [priceReason, setPriceReason] = useState('');
  const [edited, setEdited] = useState(false);
  const sen = parseRM(price);
  const needsPrice = l.needsPrice ?? l.unitPriceSen === 0;
  const cancelPrice = () => {
    setPendingSen(null);
    setPriceReason('');
    setEdited(false);
    setPrice(senToInput(l.unitPriceSen));
  };
  const cover = payerName ? (
    <button
      type="button"
      className={styles.chipButton}
      disabled={!editable}
      aria-label={`${l.description}: ${l.covered ? `covered by ${payerName}` : 'excluded, patient pays'}${editable ? ' (change)' : ''}`}
      onClick={() => onCovered(!l.covered)}
    >
      <Badge tone={l.covered ? 'success' : 'warning'}>
        {l.covered ? `Covered by ${payerName}` : 'Excluded · patient pays'}
      </Badge>
    </button>
  ) : null;
  return (
    <tr>
      <td>
        {l.description}
        {l.origin === 'refill' && (
          <>
            {' '}
            <Badge tone="info">Refill</Badge>
          </>
        )}
        {needsPrice && (
          <>
            {' '}
            <Badge tone="warning">Needs a price</Badge>
          </>
        )}
        <br />
        <span className={styles.sub}>
          {LINE_KIND_LABEL[l.kind]}
          {l.priceSource === 'stock' && ' · price from Stock › Prices'}
          {l.priceSource === 'manual' &&
            l.priceReason &&
            ` · price changed: ${l.priceReason}`}
          {l.listUnitPriceSen !== null && (
            <>
              {' '}
              · member price (list <s>{formatRM(l.listUnitPriceSen)}</s>)
            </>
          )}
        </span>{' '}
        {cover}
      </td>
      <td>
        {editable ? (
          <Input
            aria-label={`Quantity for ${l.description}`}
            inputMode="numeric"
            value={qty}
            onChange={(ev) => setQty(ev.target.value.replace(/\D/g, ''))}
            onBlur={() => {
              const n = Number(qty);
              if (n >= 1 && n !== l.quantity) onQuantity(n);
              else setQty(String(l.quantity));
            }}
          />
        ) : (
          l.quantity
        )}
      </td>
      <td>
        {editable ? (
          <Input
            aria-label={`Price for ${l.description}`}
            inputMode="decimal"
            value={price}
            aria-invalid={sen === null}
            onChange={(ev) => {
              setPrice(ev.target.value);
              setEdited(true);
            }}
            onBlur={() => {
              if (sen === null) return cancelPrice();
              // A "Needs a price" line is priced by what is typed, even 0
              // (given free), with no reason (review fix, RL-1951).
              if (needsPrice) {
                if (edited) {
                  setEdited(false);
                  onPrice(sen);
                }
                return;
              }
              if (sen === l.unitPriceSen) return setPendingSen(null);
              // A line that has a price (from stock or by hand, also RM 0)
              // changes only with a reason.
              setPendingSen(sen);
            }}
          />
        ) : (
          formatRM(l.unitPriceSen)
        )}
        {editable && pendingSen !== null && (
          <div className={styles.sub} role="group" aria-label="Price change">
            <Input
              aria-label={`Reason for the new price of ${l.description}`}
              placeholder="Why the price changes"
              maxLength={200}
              value={priceReason}
              onChange={(ev) => setPriceReason(ev.target.value)}
            />
            <Button
              size="sm"
              disabled={priceReason.trim().length < 3}
              onClick={() => onPrice(pendingSen, priceReason.trim())}
            >
              Save price
            </Button>
            <Button size="sm" variant="ghost" onClick={cancelPrice}>
              Keep {formatRM(l.unitPriceSen)}
            </Button>
          </div>
        )}
      </td>
      <td>{formatRM(l.amountSen)}</td>
      {editable && (
        <td>
          <button
            type="button"
            className={styles.iconLink}
            aria-label={`Remove ${l.description}`}
            onClick={onRemove}
          >
            <X size={14} />
          </button>
        </td>
      )}
    </tr>
  );
}
